import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AnalyticsService } from './analytics.service';
import { environment } from '../../environments/environment';

// =====================================================
// PACOTE SELECIONADO
// =====================================================

export interface PacoteSelecionado {
  id: number;
  nome: string;
  valor: number;
}

// =====================================================
// HÓSPEDE
// =====================================================

export interface Hospede {
  nome: string;
  sobrenome: string;
  email: string;
  telefone: string;
  cpf: string;
}

// =====================================================
// PARCELAMENTO
// =====================================================

export interface OpcaoParcelamento {
  parcelas: number;
  valorTotal: number;
  valorParcela: number;
}

// =====================================================
// RESPOSTA DO CHECKOUT
// =====================================================

export interface CheckoutReservaResponse {
  reservaId: number;
  pagamentoId: number;
  checkoutUrl?: string | null;
  pixQrCode?: string | null;
  pixCopiaCola?: string | null;
  status: string;
  expiraEm?: string | null;
}

// =====================================================
// CALENDÁRIO
// =====================================================

export interface ReservaCalendario {
  checkIn: string;
  checkOut: string;
  status: string;
}

// =====================================================
// PAGAMENTO
// =====================================================

export interface CheckoutPagamentoResponse
  extends CheckoutReservaResponse {}

// =====================================================
// CARTÃO
// =====================================================

export interface PagamentoCartaoRequest {
  numeroCartao: string;
  nomeCartao: string;
  validadeCartao: string;
  cvv: string;
  cpfCartao: string;
  parcelas: number;
}

// =====================================================
// ASAAS
// =====================================================

export interface AsaasCreditCardRequest {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
}

export interface AsaasCreditCardHolderInfoRequest {
  name: string;
  cpfCnpj: string;
  email: string;
  postalCode: string;
  addressNumber: string;
  phone: string;
  mobilePhone: string;
}

export interface AsaasCreditCardPaymentRequest {
  creditCard: AsaasCreditCardRequest;
  creditCardHolderInfo: AsaasCreditCardHolderInfoRequest;
  remoteIp: string;
  installmentCount: number;
}

// =====================================================
// STATUS
// =====================================================

export interface StatusPagamentoResponse {
  status: string;
}

// =====================================================
// CHECKOUT DATA
// =====================================================

export interface CheckoutData {

  // Google Analytics
  gaClientId?: string;
  gaSessionId?: number;
  // ===================================================
  // ACOMODAÇÃO
  // ===================================================

  quartoId?: number;
  quartoNome?: string;
  quartoImagem?: string;

  // ===================================================
  // DATAS
  // ===================================================

  checkIn?: string;
  checkOut?: string;

  // ===================================================
  // HÓSPEDES
  // ===================================================

  adultos?: number;
  criancas?: number;
  animais?: number;
  quantidadeHospedes?: number;
  quantidadeNoites?: number;

  // ===================================================
  // VALORES
  // ===================================================

  valorDiaria?: number;
  valorTotal?: number;

  // ===================================================
  // PACOTES
  // ===================================================

  pacotesSelecionados?: PacoteSelecionado[];
  valorPacotes?: number;

  // ===================================================
  // CUPOM
  // ===================================================

  codigoCupom?: string | null;
  valorOriginal?: number;
  valorDesconto?: number;

  // ===================================================
  // HÓSPEDES CADASTRADOS
  // ===================================================

  hospedes?: Hospede[];

  // ===================================================
  // ENDEREÇO
  // ===================================================

  cep?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;

  // ===================================================
  // PAGAMENTO
  // ===================================================

  formaPagamento?: 'CARTAO' | 'PIX' | null;
  numeroCartao?: string;
  nomeCartao?: string;
  validadeCartao?: string;
  cvv?: string;
  cpfCartao?: string;
  parcelas?: number;

  // ===================================================
  // RESERVA
  // ===================================================

  reservaId?: number;
  pagamentoId?: number;
  checkoutUrl?: string | null;
  statusPagamento?: string;
  pixQrCode?: string | null;
  pixCopiaCola?: string | null;
  expiraEm?: string | null;
}

// =====================================================
// RESPOSTA DA RESERVA
// =====================================================

export interface ReservaResponse {
  id: number;
  quartoId: number;
  quartoNome: string;

  quantidadeHospedes: number;
  quantidadeCriancas: number;
  quantidadeAnimais: number;

  checkIn: string;
  checkOut: string;
  quantidadeNoites: number;

  valorOriginal: number;
  valorDesconto: number;
  codigoCupom?: string | null;
  valorTotal: number;

  status: string;

  hospedes?: Hospede[];
}

// =====================================================
// DISPONIBILIDADE
// =====================================================

export interface DisponibilidadeResponse {
  disponivel: boolean;
}

// =====================================================
// SERVICE
// =====================================================

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {

  private apiUrl = environment.apiUrl;

  dados: CheckoutData = {};

  constructor(
    private http: HttpClient,
    private analyticsService: AnalyticsService
  ) {}

  // =====================================================
  // ATUALIZAR CHECKOUT
  // =====================================================

  atualizar(
    novosDados: Partial<CheckoutData>
  ): void {
    this.dados = {
      ...this.dados,
      ...novosDados
    };
  }

  // =====================================================
  // OBTER DADOS
  // =====================================================

  obter(): CheckoutData {
    return this.dados;
  }

  // =====================================================
  // DATAS OCUPADAS
  // =====================================================

  buscarDatasOcupadas(
    quartoId: number
  ): Observable<ReservaCalendario[]> {

    return this.http.get<ReservaCalendario[]>(
      `${this.apiUrl}/reservas/calendario`,
      {
        params: {
          quartoId: quartoId.toString()
        }
      }
    );
  }

  // =====================================================
  // OPÇÕES DE PARCELAMENTO
  // =====================================================

  obterOpcoesParcelamento(
    reservaId: number
  ): Observable<OpcaoParcelamento[]> {

    return this.http.get<OpcaoParcelamento[]>(
      `${this.apiUrl}/reservas/${reservaId}/checkout/cartao/opcoes`
    );
  }

  // =====================================================
  // DISPONIBILIDADE
  // =====================================================

  verificarDisponibilidade(
    quartoId: number,
    checkIn: string,
    checkOut: string
  ): Observable<DisponibilidadeResponse> {

    return this.http.get<DisponibilidadeResponse>(
      `${this.apiUrl}/reservas/disponibilidade`,
      {
        params: {
          quartoId: quartoId.toString(),
          checkIn,
          checkOut
        }
      }
    );
  }

  // =====================================================
  // CRIAR RESERVA
  // =====================================================

  criarReserva(): Observable<ReservaResponse> {
    return from(this.analyticsService.obterIdentificadores()).pipe(
      switchMap(identificadores => {
        this.atualizar(identificadores);

        // Mantenha aqui a montagem atual de hospedes,
        // hospedesPayload, pacotesIds e body.

        const hospedes = this.dados.hospedes || [];

        const hospedesPayload = hospedes.map(hospede => ({
          nome: hospede.nome,
          sobrenome: hospede.sobrenome,
          email: hospede.email,
          telefone: hospede.telefone,
          cpf: hospede.cpf
        }));

        const pacotesIds = (this.dados.pacotesSelecionados || [])
          .map(pacote => pacote.id)
          .filter(id => !!id);

        const body = {
          quartoId: this.dados.quartoId,
          gaClientId: this.dados.gaClientId ?? null,
          gaSessionId: this.dados.gaSessionId ?? null,
          quantidadeHospedes: this.dados.adultos ?? 1,
          quantidadeCriancas: this.dados.criancas ?? 0,
          quantidadeAnimais: this.dados.animais ?? 0,
          checkIn: this.dados.checkIn,
          checkOut: this.dados.checkOut,
          codigoCupom: this.dados.codigoCupom || null,
          pacotesIds,
          hospedes: hospedesPayload
        };

        return this.http.post<ReservaResponse>(
          `${this.apiUrl}/reservas`,
          body
        );
      })
    );
  }

  // =====================================================
  // SALVAR RESERVA
  // =====================================================

  salvarReserva(
    reserva: ReservaResponse
  ): void {

    this.dados = {
      ...this.dados,

      reservaId: reserva.id,

      /*
       * Os valores oficiais são os calculados
       * pelo backend.
       */

      valorOriginal: reserva.valorOriginal,
      valorDesconto: reserva.valorDesconto,
      codigoCupom: reserva.codigoCupom,
      valorTotal: reserva.valorTotal,
      quantidadeNoites: reserva.quantidadeNoites
    };
  }

  // =====================================================
  // PAGAMENTO PIX
  // =====================================================

  criarPagamentoPix(
    reservaId: number
  ): Observable<CheckoutReservaResponse> {

    return this.http.post<CheckoutReservaResponse>(
      `${this.apiUrl}/reservas/${reservaId}/checkout`,
      {}
    );
  }

  // =====================================================
  // SALVAR PAGAMENTO
  // =====================================================

  salvarPagamento(
    pagamento: CheckoutReservaResponse
  ): void {

    this.dados = {
      ...this.dados,

      reservaId: pagamento.reservaId,
      pagamentoId: pagamento.pagamentoId,
      checkoutUrl: pagamento.checkoutUrl,
      statusPagamento: pagamento.status,
      pixQrCode: pagamento.pixQrCode,
      pixCopiaCola: pagamento.pixCopiaCola,
      expiraEm: pagamento.expiraEm
    };
  }

  // =====================================================
  // VERIFICAR STATUS
  // =====================================================

  verificarStatusPagamento(
    reservaId: number
  ): Observable<StatusPagamentoResponse> {

    return this.http.get<StatusPagamentoResponse>(
      `${this.apiUrl}/reservas/${reservaId}/pagamento/status`
    );
  }

  // =====================================================
  // CONSULTAR STATUS
  // =====================================================

  consultarStatusPagamento(
    reservaId: number
  ): Observable<CheckoutReservaResponse> {

    return this.http.get<CheckoutReservaResponse>(
      `${this.apiUrl}/reservas/${reservaId}/pagamento/status`
    );
  }

  // =====================================================
  // PAGAMENTO CARTÃO
  // =====================================================

  criarPagamentoCartao(
    reservaId: number,
    pagamento: PagamentoCartaoRequest
  ): Observable<CheckoutReservaResponse> {

    // ===================================================
    // VALIDAÇÕES
    // ===================================================

    if (!pagamento.numeroCartao) {
      throw new Error(
        'Número do cartão não informado.'
      );
    }

    if (!pagamento.nomeCartao) {
      throw new Error(
        'Nome do titular não informado.'
      );
    }

    if (!pagamento.validadeCartao) {
      throw new Error(
        'Validade do cartão não informada.'
      );
    }

    if (!pagamento.cvv) {
      throw new Error(
        'CVV não informado.'
      );
    }

    if (
      !pagamento.parcelas ||
      pagamento.parcelas < 1
    ) {
      throw new Error(
        'Quantidade de parcelas inválida.'
      );
    }

    // ===================================================
    // VALIDADE
    // ===================================================

    const validade = pagamento.validadeCartao
      .replace(/\s/g, '')
      .split('/');

    if (validade.length !== 2) {
      throw new Error(
        'Validade do cartão inválida. Use MM/AA.'
      );
    }

    const expiryMonth = validade[0].padStart(2, '0');

    let expiryYear = validade[1];

    if (expiryYear.length === 2) {
      expiryYear = `20${expiryYear}`;
    }

    // ===================================================
    // PRIMEIRO HÓSPEDE
    // ===================================================

    const hospede = this.dados.hospedes?.[0];

    if (!hospede) {
      throw new Error(
        'Nenhum hóspede encontrado para o pagamento.'
      );
    }

    // ===================================================
    // BODY ASAAS
    // ===================================================

    const body: AsaasCreditCardPaymentRequest = {

      creditCard: {
        holderName: pagamento.nomeCartao,

        number: pagamento.numeroCartao
          .replace(/\s/g, ''),

        expiryMonth,
        expiryYear,

        ccv: pagamento.cvv
      },

      creditCardHolderInfo: {

        name: `${hospede.nome} ${hospede.sobrenome}`.trim(),

        cpfCnpj:
          pagamento.cpfCartao ||
          hospede.cpf,

        email: hospede.email,

        postalCode:
          this.dados.cep || '',

        addressNumber:
          this.dados.numero || '',

        phone: hospede.telefone,

        mobilePhone: hospede.telefone
      },

      remoteIp: '127.0.0.1',

      installmentCount:
      pagamento.parcelas
    };

    // ===================================================
    // POST CARTÃO
    // ===================================================

    return this.http.post<CheckoutReservaResponse>(
      `${this.apiUrl}/reservas/${reservaId}/checkout/cartao`,
      body
    );
  }

  // =====================================================
  // LIMPAR CHECKOUT
  // =====================================================

  limpar(): void {
    this.dados = {};
  }
}
